import { useState } from "react";
import { LineChart } from "@mui/x-charts";
import { useTranslation } from "react-i18next";

export default function UserChartsUi({ value, onFilterChange }) {
    const { t } = useTranslation();
    const currentYear = new Date().getFullYear();

    // --- State for Chart 1: Daily Activity (Range Filter) ---
    const [activityRange, setActivityRange] = useState(null);

    // --- State for Chart 2: User Growth (Year/Month Filter) ---
    const [growthYear, setGrowthYear] = useState(null);
    const [growthMonth, setGrowthMonth] = useState(null);

    // --- Data Arrays ---
    const years = Array.from({ length: 10 }, (_, i) => currentYear - i);
    const months = [
        { value: "1", label: t("January") || "January" },
        { value: "2", label: t("February") || "February" },
        { value: "3", label: t("March") || "March" },
        { value: "4", label: t("April") || "April" },
        { value: "5", label: t("May") || "May" },
        { value: "6", label: t("June") || "June" },
        { value: "7", label: t("July") || "July" },
        { value: "8", label: t("August") || "August" },
        { value: "9", label: t("September") || "September" },
        { value: "10", label: t("October") || "October" },
        { value: "11", label: t("November") || "November" },
        { value: "12", label: t("December") || "December" },
    ];

    // --- Handlers ---
    const handleActivityFilterChange = (e) => {
        const newRange = e.target.value;
        setActivityRange(newRange);
        if (onFilterChange) {
            onFilterChange("activity", { range: newRange });
        }
    };

    const handleGrowthYearChange = (e) => {
        const newYear = e.target.value;
        setGrowthYear(newYear);
        setGrowthMonth(""); 
        if (onFilterChange) {
            onFilterChange("growth", { year: newYear, month: null });
        }
    };

    const handleGrowthMonthChange = (e) => {
        const newMonth = e.target.value;
        setGrowthMonth(newMonth);
        setGrowthYear(""); 
        if (onFilterChange) {
            onFilterChange("growth", { year: null, month: newMonth });
        }
    };

    // --- Process Data ---
    const hoursData = value?.visit_by_hours || [];
    const hoursLabels = hoursData.map(item => item.hour);
    const hoursVisits = hoursData.map(item => item.visits);

    const growthData = value?.user_by_time || [];
    const growthLabels = growthData.map(item => item.time);
    const growthCounts = growthData.map(item => item.count);

    // --- 🎨 CUSTOM STYLES FOR STABLE CLASSES ---
    const chartSettingStyles = {
        // 1. Main Axis Lines
        "& .MuiChartsAxis-line": {
            stroke: "#ffffff !important",
            strokeWidth: 2,
        },
        // 2. Ticks (small lines)
        "& .MuiChartsAxis-tick": {
            stroke: "#ffffff !important",
        },
        // 3. Tick Labels (numbers/dates on axis)
        "& .MuiChartsAxis-tickLabel": {
            fill: "#ffffff !important",
            fontWeight: "500",
        },
        // 4. Grid Lines
        "& .MuiChartsGrid-line": {
            stroke: "rgba(255, 255, 255, 0.2) !important",
        },
        // 5. Axis Labels (The words "Hours . Visitors", etc.)
        "& .MuiChartsAxis-label": {
             fill: "#ffffff !important",
             fontWeight: "bold",
        }
    };

    return (
        <>
            {/* ---------------- CHART 1: DAILY ACTIVITY ---------------- */}
            <div className='chart-wrap w-full bg-thirdly flex flex-col justify-center gap-3 px-5 pt-6 pb-3 my-5 rounded-lg'>
                
                {/* Header */}
                <div className="flex sm:flex-row flex-col-reverse justify-between items-center mb-2 gap-4">
                    <div className="flex gap-3">
                        <select
                            value={activityRange || ""}
                            onChange={handleActivityFilterChange}
                            className="bg-white/10 border border-white/20 text-white text-sm rounded-md px-3 py-1 outline-none focus:border-blue-500 cursor-pointer"
                        >
                            <option value="" disabled className="text-black">Select Month</option>
                            <option value="3" className="text-black">{t("Last 3 Months")}</option>
                            <option value="6" className="text-black">{t("Last 6 Months")}</option>
                            <option value="9" className="text-black">{t("Last 9 Months")}</option>
                            <option value="12" className="text-black">{t("Last 12 Months")}</option>
                        </select>
                    </div>
                    <label className='text-white font-semibold text-lg text-right'>
                        {t("charts.daily_activity_chart")}
                    </label>
                </div>

                <LineChart
                    xAxis={[{
                        data: hoursLabels,
                        scaleType: 'point',
                        // OPTIMIZATION 1: Show fewer labels (e.g., every 3rd hour) to reduce crowding
                        tickInterval: (value, index) => index % 3 === 0,
                    }]}
                    yAxis={[{ 
                        // OPTIMIZATION 2: Simpler label
                        label: "Visitors", 
                    }]}
                    series={[{
                        data: hoursVisits,
                        color: "#3b82f6",
                        area: true, // Keep the area glow, it looks nice
                        curve: "natural", 
                        // OPTIMIZATION 3: Hide the dots to clean up the line
                        showMark: false, 
                    }]}
                    height={370}
                    // OPTIMIZATION 4: Remove vertical lines, keep horizontal for reference
                    grid={{ vertical: false, horizontal: true }}
                    
                    // Apply your White/Dark theme styles
                    sx={{
                        ...chartSettingStyles, // Use the same styles object from previous answer
                        // Optional: Extra padding for the Y-axis label if it feels too close
                        "& .MuiChartsAxis-left .MuiChartsAxis-label": {
                            transform: "translateX(-10px)", // Moves "Visitors" slightly left
                        }
                    }}
                />
            </div>
            {/* ---------------- CHART 2: USER GROWTH ---------------- */}
            <div className='chart-wrap w-full bg-thirdly flex flex-col justify-center gap-3 px-5 pt-6 pb-3 my-5 rounded-lg'>
                
                {/* Header */}
                <div className="flex sm:flex-row flex-col-reverse justify-between items-center mb-2 gap-4">
                    <div className="flex gap-3">
                        <select
                            value={growthYear || ""}
                            onChange={handleGrowthYearChange}
                            className="bg-white/10 border border-white/20 text-white text-sm rounded-md px-3 py-1 outline-none focus:border-blue-500 cursor-pointer"
                        >
                            <option value="" disabled className="text-gray-500">{t("Select Year")}</option>
                            {years.map((year) => (
                                <option key={year} value={year} className="text-black">{year}</option>
                            ))}
                        </select>

                        <select
                            value={growthMonth || ""}
                            onChange={handleGrowthMonthChange}
                            className="bg-white/10 border border-white/20 text-white text-sm rounded-md px-3 py-1 outline-none focus:border-blue-500 cursor-pointer"
                        >
                            <option value="" disabled className="text-gray-500">{t("Select Month")}</option>
                            {months.map((m) => (
                                <option key={m.value} value={m.value} className="text-black">{m.label}</option>
                            ))}
                        </select>
                    </div>

                    <label className='text-white font-semibold text-lg text-right'>
                        {t("charts.user_growth_chart")}
                    </label>
                </div>

                <LineChart
                    xAxis={[{
                        data: growthLabels,
                        scaleType: 'point',
                    }]}
                    yAxis={[{ label: "Days . No. Users" }]}
                    series={[{
                        data: growthCounts,
                        color: "#3b82f6",
                        area: true,
                        curve: "natural",
                    }]}
                    height={370}
                    grid={{ vertical: true, horizontal: true }}
                    // Apply styles here
                    sx={chartSettingStyles}
                />
            </div>
        </>
    );
};